import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Download,
  Mail,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

const TEAL = '#004C54';
const TEAL_DEEP = '#003a40';
const CYAN = '#4FBCC7';
const CREAM = '#F5F3EE';

const DEFAULT_PDF = '/mental-performance-checklist.pdf';

// Sanitize the ?pdf= query string parameter so an attacker can\u2019t pass an
// off-domain link via the URL. Accepts: relative .pdf paths OR apex domain
// URLs. Anything else falls back to the bundled default.
function safePdfUrl(fromQuery) {
  if (!fromQuery) return DEFAULT_PDF;
  if (/^\/[a-z0-9-]+\.pdf$/i.test(fromQuery)) return fromQuery;
  if (fromQuery.startsWith('http')) {
    try {
      const u = new URL(fromQuery);
      if (u.hostname.endsWith('apexathleticmind.com') || u.hostname.includes('apex')) {
        return fromQuery;
      }
    } catch {
      /* malformed URL \u2014 fall through */
    }
  }
  return DEFAULT_PDF;
}

export default function ChecklistThankYou() {
  const [params] = useSearchParams();
  // Compute during render \u2014 deterministic, no effect needed.
  const pdfUrl = safePdfUrl(params.get('pdf'));

  return (
    <div
      className="min-h-screen"
      style={{ background: CREAM }}
      data-testid="checklist-thankyou-page"
    >
      {/* Top utility bar */}
      <div
        style={{ background: TEAL, color: 'rgba(255,255,255,0.85)' }}
        className="text-[12px]"
      >
        <div className="max-w-[1120px] mx-auto px-5 py-2 flex items-center justify-between">
          <Link
            to="/"
            className="font-mono-accent uppercase tracking-[0.18em] hover:text-white"
            data-testid="checklist-thankyou-back-home"
          >
            ← apexathleticmind.com
          </Link>
        </div>
      </div>

      {/* ===== HERO: success state ===== */}
      <section>
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 pt-14 pb-10 sm:pt-20 sm:pb-12 text-center">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
            style={{ background: TEAL, color: CYAN }}
            data-testid="checklist-thankyou-icon"
          >
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <div
            className="font-mono-accent uppercase tracking-[0.26em] text-[10.5px] font-bold mb-3"
            style={{ color: CYAN }}
          >
            You’re in
          </div>
          <h1
            className="font-heading text-[2rem] sm:text-[2.6rem] font-bold tracking-tight leading-[1.1] mb-4"
            style={{ color: TEAL }}
            data-testid="checklist-thankyou-title"
          >
            Your checklist is ready.
          </h1>
          <p
            className="text-[15.5px] sm:text-[17px] text-[#1a1a1a] leading-relaxed max-w-[520px] mx-auto mb-8"
            data-testid="checklist-thankyou-sub"
          >
            I just emailed you a copy. You can also download it right now — click below.
          </p>

          {/* Primary download CTA */}
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="checklist-thankyou-download-link"
          >
            <Button
              className="h-14 px-8 font-bold text-[15px]"
              style={{ background: TEAL, color: '#fff' }}
              data-testid="checklist-thankyou-download-btn"
            >
              <Download className="h-5 w-5 mr-2" />
              Download the Checklist (PDF)
            </Button>
          </a>

          <p className="text-[12px] text-[#666] mt-4" data-testid="checklist-thankyou-email-note">
            <Mail className="inline h-3.5 w-3.5 mr-1" style={{ color: TEAL }} />
            Also check your inbox — may take 1–2 minutes. Look for “Your Mental Performance Checklist is inside.”
          </p>
        </div>
      </section>

      {/* ===== CROSS-POLLINATION: Mindset Quiz tease (Phase 33) ===== */}
      <section
        className="border-y"
        style={{
          background: '#0a0d0e',
          borderColor: 'rgba(79,188,199,0.18)',
        }}
      >
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <div
            className="rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5"
            style={{
              background: 'linear-gradient(135deg, #004C54 0%, #003a40 100%)',
              border: '1px solid rgba(79,188,199,0.25)',
            }}
            data-testid="checklist-thankyou-quiz-cross"
          >
            <div className="flex-1 min-w-0">
              <div
                className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] font-bold mb-2"
                style={{ color: CYAN }}
              >
                Next step · 2 minutes
              </div>
              <h3
                className="font-heading text-[1.3rem] sm:text-[1.55rem] font-bold tracking-tight text-white mb-1.5 leading-tight"
                data-testid="checklist-thankyou-quiz-cross-title"
              >
                Want to know exactly which mindset gap to train first?
              </h3>
              <p className="text-[13.5px] text-white/75 leading-relaxed max-w-[42ch]">
                The Mindset Quiz reveals your personal Mental Edge Score across 6 dimensions — plus the #1 protocol to run from the checklist you just downloaded.
              </p>
            </div>
            <Link
              to="/quiz?src=checklist_ty"
              data-testid="checklist-thankyou-quiz-cross-link"
              className="flex-shrink-0"
            >
              <Button
                className="h-11 px-5 font-bold text-[13.5px]"
                style={{ background: '#fff', color: TEAL }}
                data-testid="checklist-thankyou-quiz-cross-btn"
              >
                Take the Mindset Quiz
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHILE YOU WAIT: AI Coach upsell ===== */}
      <section
        className="bg-white border-y"
        style={{ borderColor: 'rgba(0,76,84,0.1)' }}
      >
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="text-center mb-8">
            <div
              className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold mb-2"
              style={{ color: CYAN }}
            >
              While the email lands
            </div>
            <h2
              className="font-heading text-[1.6rem] sm:text-[2rem] font-bold tracking-tight leading-tight"
              style={{ color: TEAL }}
              data-testid="checklist-thankyou-upsell-title"
            >
              Want the full system?
            </h2>
          </div>
          <div
            className="rounded-2xl p-6 sm:p-8 border"
            style={{ background: CREAM, borderColor: 'rgba(0,76,84,0.1)' }}
          >
            <div className="flex items-start gap-4 mb-4">
              <div
                className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: TEAL, color: CYAN }}
              >
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h3
                  className="font-heading text-[18px] font-bold mb-1"
                  style={{ color: TEAL }}
                >
                  Apex AI Coach — 7 days free, no card
                </h3>
                <p className="text-[13.5px] text-[#3a3a3a] leading-relaxed">
                  Chat with an AI coach trained on Coach Lewis’s exact protocols. Pre-game plans, journal reflections, all in Coach Lewis’s voice. Start free, no credit card.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 sm:gap-4 mt-5">
              <Link
                to="/app/signup"
                data-testid="checklist-thankyou-trial-link"
              >
                <Button
                  className="h-11 px-5 font-bold text-[13.5px]"
                  style={{ background: TEAL, color: '#fff' }}
                  data-testid="checklist-thankyou-trial-btn"
                >
                  Start Free →
                </Button>
              </Link>
              <Link
                to="/method"
                data-testid="checklist-thankyou-method-link"
              >
                <Button
                  variant="outline"
                  className="h-11 px-5 font-bold text-[13.5px]"
                  style={{ borderColor: TEAL, color: TEAL }}
                  data-testid="checklist-thankyou-method-btn"
                >
                  Explore The Apex Six
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW TO USE THE CHECKLIST ===== */}
      <section style={{ background: CREAM }}>
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <h2
            className="font-heading text-[1.5rem] sm:text-[1.8rem] font-bold tracking-tight leading-tight mb-6 text-center"
            style={{ color: TEAL }}
            data-testid="checklist-thankyou-howto-title"
          >
            How to use the checklist this week
          </h2>
          <ol
            className="space-y-4"
            data-testid="checklist-thankyou-howto-list"
          >
            {[
              ['Print it.', 'Pin it to your locker, gym bag, or bedroom wall — somewhere you’ll see it before competition.'],
              ['Pick ONE protocol.', 'Don’t try all six at once. Start with the 3-Breath Reset. Run it before your next practice.'],
              ['Fill it in after games.', 'The Post-Competition Reflection at the back is where the reps compound.'],
              ['Watch your inbox.', 'I’ll send a short email series this week with the same tools I use with state champions.'],
            ].map(([title, body], i) => (
              <li
                key={title}
                className="flex gap-4 items-start"
                data-testid={`checklist-thankyou-howto-step-${i + 1}`}
              >
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-[14px]"
                  style={{ background: TEAL, color: '#fff' }}
                >
                  {i + 1}
                </div>
                <div>
                  <div
                    className="font-heading font-bold text-[15.5px] mb-0.5"
                    style={{ color: TEAL }}
                  >
                    {title}
                  </div>
                  <p className="text-[13.5px] text-[#3a3a3a] leading-relaxed">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="text-center mt-10">
            <Link to="/" data-testid="checklist-thankyou-home-link">
              <Button
                variant="ghost"
                className="text-[13px] font-semibold"
                style={{ color: TEAL }}
                data-testid="checklist-thankyou-home-btn"
              >
                Back to apexathleticmind.com
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer
        style={{ background: TEAL_DEEP }}
        className="text-white/55 text-[12px]"
      >
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-6 text-center">
          © {new Date().getFullYear()} Apex Athletic Mind Consulting LLC · Coach Varsay Lewis, M.Ed.
        </div>
      </footer>
    </div>
  );
}

export { ChecklistThankYou };
